import { useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import clsx from 'clsx'
import { ChevronDownIcon } from '../icons/icons'
import { createLayoutResizeObserver } from '../utils/layoutResizeObserver'
import { FieldSelectorTrigger } from './FieldSelectorTrigger'
import InfoHint from './InfoHint'
import { ValueSelectorDisabledIndicator, ValueSelectorSecondaryLabel } from './ValueSelectorParts'
import type { ValueSelectorOption } from './ValueSelector'
import { optionToneTextClass } from './pageHeaderStatusTones'

export type ValueSelectorVariant = 'toolbar' | 'field' | 'cell' | 'compound' | 'viewParameter'

interface ValueSelectorTriggerProps {
  id?: string
  open: boolean
  searchInTrigger: boolean
  search: string
  onSearchChange: (value: string) => void
  onOpenChange: (open: boolean) => void
  /** Click on the built-in button trigger; decides whether it toggles. */
  onTriggerClick: (event: MouseEvent<HTMLElement>) => void
  onDropdownKeyDown: (event: KeyboardEvent) => void
  onTriggerKeyDown: (event: KeyboardEvent) => void
  onBlur?: () => void
  placeholder: string
  placeholderClassName?: string
  selectedOption?: ValueSelectorOption
  /** The selected option's description, when the closed trigger repeats it. */
  selectedDescription?: string
  /** Options are cards: the chosen one's description is the closed field's tip. */
  optionCards?: boolean
  renderTrigger?: (args: { open: boolean; toggle: () => void }) => ReactNode
  variant: ValueSelectorVariant
  inlineLabel?: string
  disabled: boolean
  disabledTooltip?: ReactNode
  ariaLabel?: string
  ariaInvalid?: boolean
  ariaRequired?: boolean
  /** Ids of the text that explains the control, such as why it is locked. */
  ariaDescribedBy?: string
  controlClassName?: string
  searchInputClassName: string
  onInputRef: (node: HTMLInputElement | null) => void
  setMeasuredElement: (node: HTMLInputElement | HTMLButtonElement | HTMLDivElement | null) => void
}

export function ValueSelectorTrigger({
  id,
  open,
  searchInTrigger,
  search,
  onSearchChange,
  onOpenChange,
  onTriggerClick,
  onDropdownKeyDown,
  onTriggerKeyDown,
  onBlur,
  placeholder,
  placeholderClassName,
  selectedOption,
  selectedDescription,
  optionCards = false,
  renderTrigger,
  variant,
  inlineLabel,
  disabled,
  disabledTooltip,
  ariaLabel,
  ariaInvalid,
  ariaRequired,
  ariaDescribedBy,
  controlClassName,
  searchInputClassName,
  onInputRef,
  setMeasuredElement,
}: ValueSelectorTriggerProps) {
  const isFieldVariant = variant === 'field'
  const isCellVariant = variant === 'cell'
  const isCompoundVariant = variant === 'compound'
  const isViewParameterVariant = variant === 'viewParameter'
  const fieldLikeVariant = isFieldVariant || isCellVariant || isCompoundVariant
  // A field's trigger isn't a button, so the selected option's tip can sit in it.
  const triggerHint = isFieldVariant
    ? (selectedOption?.hint ?? (optionCards ? selectedOption?.description : undefined))
    : undefined
  const secondaryFits = useSecondaryLabelFits(
    selectedOption?.secondaryLabel ? selectedOption : undefined,
    selectedDescription
  )
  const baseControlClassName = fieldLikeVariant
    ? isCompoundVariant
      ? 'relative flex h-9 w-full items-center justify-between rounded-none border-0 py-0 pl-2 pr-0 text-left text-xs font-semibold outline-none transition-colors'
      : clsx(
          'field-input-control relative flex w-full items-center justify-between text-left text-sm outline-none transition-colors',
          isCellVariant
            ? 'h-11 rounded-none border-0 py-0 pl-5 pr-10'
            : 'rounded-md border py-2 pl-3 pr-10'
        )
    : clsx(
        'flex h-8 w-full items-center justify-between rounded-md border px-2 transition-colors focus-visible:border-blue-500 focus-visible:outline-none',
        isViewParameterVariant ? 'text-view-parameter-control' : 'text-toolbar'
      )
  // Each state sets every color once, so CSS order never decides between them.
  const controlState = disabled ? 'disabled' : open ? 'open' : 'idle'
  const stateControlClassName = fieldLikeVariant
    ? isCompoundVariant
      ? {
          idle: 'surface-default text-subtle hover:bg-lighter',
          open: 'bg-lighter text-subtle',
          disabled: 'bg-lighter text-muted',
        }[controlState]
      : isCellVariant
        ? {
            // A grid cell takes its color from the cell except while its list is open.
            idle: 'bg-transparent',
            open: 'bg-[color:var(--surface-info-subtle)] ring-1 ring-inset ring-[color:var(--border-info)]',
            disabled: 'bg-transparent text-muted',
          }[controlState]
        : {
            idle: 'border-[color:var(--input-border)] bg-[color:var(--input-bg)] text-[color:var(--input-text)] hover:border-[color:var(--border-info)] hover:bg-[color:color-mix(in_srgb,var(--surface-info-subtle)_50%,transparent)]',
            open: 'border-blue-500 bg-[color:var(--input-bg)] text-[color:var(--input-text)] hover:border-[color:var(--border-info)] hover:bg-[color:color-mix(in_srgb,var(--surface-info-subtle)_50%,transparent)]',
            disabled: 'border-[color:var(--input-border)] bg-lighter text-muted',
          }[controlState]
    : {
        idle: 'border-[color:var(--border-strong)] hover:border-[color:var(--border-emphasis)]',
        open: 'border-blue-500 hover:border-[color:var(--border-emphasis)]',
        disabled: 'border-[color:var(--border-strong)] bg-lighter text-data',
      }[controlState]
  const standardTriggerValue = (
    <span ref={secondaryFits.valueRef} className="flex min-w-0 flex-1 items-center gap-2 truncate">
      {selectedOption ? (
        <>
          {selectedOption.leading ? (
            <span className="flex shrink-0 items-center">{selectedOption.leading}</span>
          ) : null}
          <span className="flex min-w-0 flex-col text-left">
            <span
              data-trigger-label="true"
              className={clsx(
                'block min-w-0 truncate',
                selectedOption.secondaryLabel && 'font-medium',
                selectedOption.muted
                  ? 'text-muted'
                  : disabled
                    ? 'text-muted'
                    : (optionToneTextClass(selectedOption.tone) ??
                      (!isCellVariant && 'text-default')),
                selectedOption.italic && 'italic'
              )}
              // eslint-disable-next-line no-restricted-syntax -- A truncated value's full text.
              title={typeof selectedOption.label === 'string' ? selectedOption.label : undefined}
            >
              {selectedOption.label}
            </span>
            {selectedDescription ? (
              <span
                data-trigger-label="true"
                className="block truncate text-xs leading-4 font-normal text-muted"
              >
                {selectedDescription}
              </span>
            ) : null}
          </span>
          {selectedOption.secondaryLabel ? (
            // At the right while it fits beside the value whole; otherwise it's
            // left out (kept, unseen, to measure).
            <span
              data-trigger-secondary="true"
              aria-hidden={!secondaryFits.fits || undefined}
              className={clsx('ml-auto flex shrink-0', !secondaryFits.fits && 'invisible absolute')}
            >
              <ValueSelectorSecondaryLabel
                option={selectedOption}
                wraps={false}
                className={disabled ? 'text-faint' : 'text-muted'}
              />
            </span>
          ) : null}
          {triggerHint ? (
            // The tip's press and keys are its own, never opening the list.
            <span
              className={clsx('inline-flex shrink-0', !selectedOption.secondaryLabel && 'ml-auto')}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <InfoHint content={triggerHint} label={`About ${selectedOption.label}`} size="sm" />
            </span>
          ) : null}
        </>
      ) : (
        <span
          className={clsx(
            isFieldVariant && 'font-light italic [font-synthesis:style]',
            isCellVariant && 'italic',
            placeholderClassName ?? 'text-muted'
          )}
        >
          {placeholder}
        </span>
      )}
    </span>
  )

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ open, toggle: () => onOpenChange(!open) })
      ) : open && searchInTrigger && !isCompoundVariant ? (
        <div className="relative">
          <input
            ref={onInputRef}
            id={id}
            type="text"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-invalid={ariaInvalid || undefined}
            aria-required={ariaRequired || undefined}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={onDropdownKeyDown}
            onBlur={onBlur}
            placeholder={placeholder}
            className={clsx(searchInputClassName, controlClassName)}
          />
          <button
            type="button"
            aria-label="Close selector"
            disabled={disabled}
            className={clsx(
              'absolute right-2 top-1/2 flex -translate-y-1/2',
              disabled ? 'text-faint' : 'text-icon-quiet hover:text-data'
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onOpenChange(false)}
          >
            <ChevronDownIcon size={12} className="rotate-180" />
          </button>
        </div>
      ) : isFieldVariant ? (
        <FieldSelectorTrigger
          ref={setMeasuredElement}
          id={id}
          open={open}
          onOpenChange={onOpenChange}
          disabled={disabled}
          ariaLabel={ariaLabel}
          ariaInvalid={ariaInvalid}
          ariaRequired={ariaRequired}
          ariaDescribedBy={ariaDescribedBy}
          onKeyDown={onTriggerKeyDown}
          onBlur={onBlur}
          className={clsx(disabled && disabledTooltip && 'pr-8', controlClassName)}
        >
          {standardTriggerValue}
        </FieldSelectorTrigger>
      ) : (
        <button
          ref={setMeasuredElement}
          id={id}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={ariaInvalid || undefined}
          aria-required={ariaRequired || undefined}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          onClick={onTriggerClick}
          onKeyDown={onTriggerKeyDown}
          onBlur={onBlur}
          className={clsx(
            baseControlClassName,
            stateControlClassName,
            disabled &&
              fieldLikeVariant &&
              (disabledTooltip && !isCompoundVariant ? 'pr-8' : 'pr-3'),
            disabled && 'cursor-not-allowed opacity-100',
            controlClassName
          )}
        >
          {isCompoundVariant ? (
            <>
              <span className="flex min-w-0 flex-1 flex-col justify-center px-0">
                {inlineLabel ? (
                  <span className="text-[8px] font-semibold uppercase leading-[9px] tracking-[0.08em] text-muted">
                    {inlineLabel}
                  </span>
                ) : null}
                {selectedOption ? (
                  <span className="flex min-w-0 items-center gap-1.5 leading-[15px]">
                    <span
                      className={clsx(
                        'truncate',
                        selectedOption.muted
                          ? 'text-muted'
                          : disabled
                            ? 'text-muted'
                            : (optionToneTextClass(selectedOption.tone) ?? 'text-subtle'),
                        selectedOption.italic && 'italic'
                      )}
                    >
                      {selectedOption.label}
                    </span>
                  </span>
                ) : (
                  <span className={clsx('leading-[15px]', placeholderClassName ?? 'text-muted')}>
                    {placeholder}
                  </span>
                )}
              </span>
              {!disabled ? (
                <span className="flex h-full w-7 shrink-0 items-center justify-center text-faint">
                  <ChevronDownIcon
                    size={9}
                    aria-hidden="true"
                    className={clsx('transition-transform', open && 'rotate-180')}
                  />
                </span>
              ) : disabledTooltip ? (
                <ValueSelectorDisabledIndicator
                  content={disabledTooltip}
                  className="flex h-full w-7 shrink-0 items-center justify-center text-icon-quiet"
                />
              ) : null}
            </>
          ) : (
            standardTriggerValue
          )}
          {!disabled && !isCompoundVariant ? (
            <ChevronDownIcon
              size={isCellVariant ? 8 : 12}
              className={clsx(
                'flex-shrink-0 text-faint transition-transform',
                open && 'rotate-180',
                fieldLikeVariant && 'absolute right-3 top-1/2 -translate-y-1/2'
              )}
            />
          ) : null}
        </button>
      )}
      {disabled && disabledTooltip && !isCompoundVariant ? (
        <ValueSelectorDisabledIndicator
          content={disabledTooltip}
          className="absolute right-2 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center text-icon-quiet"
        />
      ) : null}
    </>
  )
}

/**
 * Whether the selected option's secondary label fits whole beside its label
 * and description, as the trigger's width changes (`createLayoutResizeObserver`,
 * so it shows or goes in the same frame). Natural widths are read from the
 * truncating spans' `scrollWidth`, so the answer doesn't depend on whether the
 * label is showing.
 */
function useSecondaryLabelFits(
  option: ValueSelectorOption | undefined,
  description: string | undefined
) {
  const valueRef = useRef<HTMLSpanElement>(null)
  const [fits, setFits] = useState(true)
  // What changes the widths, not the option object (callers rebuild it).
  const shown = option ? [option.id, option.label, option.secondaryLabel, description] : null
  const key = shown ? JSON.stringify(shown) : null
  useLayoutEffect(() => {
    const value = valueRef.current
    if (!value || key === null) return
    const measure = () => {
      const secondary = value.querySelector<HTMLElement>('[data-trigger-secondary] > span')
      if (!secondary) return
      const gap = parseFloat(getComputedStyle(value).columnGap) || 0
      const labels = value.querySelectorAll<HTMLElement>('[data-trigger-label]')
      const labelWidth = Math.max(0, ...Array.from(labels, (label) => label.scrollWidth))
      // Everything else in the row (the option's leading icon, a tip) at its size.
      const others = Array.from(value.children as HTMLCollectionOf<HTMLElement>)
        .filter((child) => !child.querySelector('[data-trigger-label]'))
        .filter((child) => !child.hasAttribute('data-trigger-secondary'))
        .reduce((total, child) => total + child.offsetWidth + gap, 0)
      setFits(labelWidth + gap + secondary.scrollWidth + others <= value.clientWidth)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = createLayoutResizeObserver(measure)
    observer.observe(value)
    return () => observer.disconnect()
  }, [key])
  return { valueRef, fits: key === null || fits }
}
